import { useField } from 'formik';
import isEqual from 'lodash/isEqual';
import { useEffect, useMemo } from 'react';

import { usePrevious } from '@hivebrite/react-hooks';

import { useGlobalStorageFiles } from 'hb-react/shared/hooks/useGlobalStorageV2';
import type { GlobalStorageFile } from 'hb-react/shared/hooks/useGlobalStorageV2/types';
import {
  getInitialFiles,
  formatRemovedFilesForSubmit,
  getStorageAttributesFieldName,
  formatAddedFilesForSubmit,
} from 'hb-react/shared/hooks/useGlobalStorageV2/utils';

const useUploadField = ({
  fieldName,
  filesValidator,
  initialContentHtml,
  markFileAsRemovedOnError,
  maxFileSize,
}: {
  fieldName: string;
  filesValidator: (value?: GlobalStorageFile[]) => string | undefined;
  initialContentHtml?: string;
  markFileAsRemovedOnError?: boolean;
  maxFileSize?: number;
}) => {
  const attributesFieldName = getStorageAttributesFieldName(fieldName);

  const [field, fieldMeta, fieldHelper] = useField({
    name: fieldName,
    validate: (value) => value?.error,
  });
  const [, , attributesFieldHelper] = useField({
    name: attributesFieldName,
  });

  const initialFiles = useMemo(
    () =>
      getInitialFiles({
        files: fieldMeta.initialValue,
        contentHtml: initialContentHtml,
      }),
    [fieldMeta.initialValue, initialContentHtml],
  );

  const { files, setFiles, addFiles, removeFiles } = useGlobalStorageFiles({
    initialFiles,
    config: {
      maxFileSize,
      markFileAsRemovedOnError,
    },
  });
  const prevFiles = usePrevious(files);

  useEffect(() => {
    const filesEmpty = !files || files.length === 0;
    const prevFilesEmpty = !prevFiles || prevFiles.length === 0;

    if (filesEmpty && prevFilesEmpty) {
      return;
    }

    if (!isEqual(files, prevFiles)) {
      const error = filesValidator(files);

      const newValue = error ? { error } : formatAddedFilesForSubmit(files);

      // Added files
      fieldHelper.setValue(newValue, true);
      fieldHelper.setTouched(true, false);

      // Removed files
      attributesFieldHelper.setValue(formatRemovedFilesForSubmit(files), false);
      attributesFieldHelper.setTouched(true, false);
    }
  }, [prevFiles, files, attributesFieldHelper, fieldHelper, filesValidator]);

  return {
    files,
    field,
    fieldMeta,
    setFiles,
    addFiles,
    removeFiles,
  };
};

export default useUploadField;
